import { AxiosResponse } from 'axios';
import { sha256 } from 'js-sha256';
import { useMutation } from 'react-query';

import { PAYMENTS_BACKEND_URL } from '@/config';
import { STRIPE_METHOD_DIRECT, UPDATE_PRODUCTS_FAKE_ID } from '@/config/appConstants';
import axios from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';

import { PaymentProps } from '..';

export type UpdatePaymentIntent = {
  status?: string;
  id: string;
  clientSecret: string;
  amount: string | number;
  currency: string;
  sessionUrl: string | null;
};

export const updatePayment = ({
  prices,
  preview = false,
  promotionCode,
  onErrorCallback,
}: PaymentProps): Promise<AxiosResponse | void> => {
  const id = preview ? 'preview' : sha256(UPDATE_PRODUCTS_FAKE_ID);
  const endPoint = `${PAYMENTS_BACKEND_URL}payment/${id}`;
  const putData = {
    prices: prices,
    method: STRIPE_METHOD_DIRECT,
    promotionCode,
  };

  return axios.put(endPoint, putData).catch((error) => {
    onErrorCallback &&
      onErrorCallback(
        error.response.status,
        error.response.data.hostedInvoiceUrl,
        error.response.data.detail
      );

    if (error.response.status === 402) {
      return;
    }

    if (error.response.status === 409) {
      return;
    }

    if (error.response.status === 503) {
      return;
    }
    if (error.response.status === 500) {
      return;
    }

    throw Error(`Critical error putting data in ${endPoint}: ${error}`);
  });
};

type UseUpdatePaymentOptions = {
  config?: MutationConfig<typeof updatePayment>;
};

export const useUpdatePayment = ({ config }: UseUpdatePaymentOptions = {}) => {
  return useMutation({
    onMutate: async (creatingNotification: any) => {
      await queryClient.cancelQueries(['payment', creatingNotification]);

      const previousDetails = queryClient.getQueryData<PaymentProps>([
        'payment',
        creatingNotification,
      ]);

      queryClient.setQueryData(['payment', creatingNotification], {
        ...previousDetails,
        ...creatingNotification.data,
      });

      return { previousDetails: previousDetails };
    },
    onError: (_, __, context: any) => {
      if (context?.previousDetails) {
        queryClient.setQueryData(['payment', context.previousDetails], context.previousDetails);
      }
    },
    onSuccess: (data) => {
      queryClient.refetchQueries(['payment', data]);
    },
    ...config,
    mutationFn: updatePayment,
  });
};
